import { Component, inject, OnInit, signal } from '@angular/core';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { FormatService } from '../../../../common/format/format.service';
import { CreateBriefPaymentMethodDto, PaymentMethodTypeEnum, VoucherTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { FormsModule } from '@angular/forms';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { ImageService } from '../../../../services/image/image.service';
import { DecimalPipe } from '@angular/common';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';

@Component({
  selector: 'agent-step-compensation',
  standalone: true,
  imports: [FormsModule, LdgImgComponent, DecimalPipe],
  templateUrl: './compensation.component.html',
  styleUrl: './compensation.component.css'
})
export class CompensationComponent implements OnInit {

  VoucherTypeEnum = VoucherTypeEnum;
  PaymentMethodTypeEnum = PaymentMethodTypeEnum;

  currencies: { code: string, symbol: string }[] = [
    { code: 'GBP', symbol: '£' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
  ];

  /** Remuneration Variables */
  remunerationOptions!: CreateBriefPaymentMethodDto[];
  /** Remuneration Variables - END */


  voucherTypeOptions: ('fixed' | 'percentage')[] = [
    'fixed',
    'percentage',
  ]

  selectedCurrency = signal<{ code: string; symbol: string } | undefined>({ code: 'GBP', symbol: '£' });
  selectedCompensationType = signal<{ id: number, name: string, description: string, icon: string, color: string } | undefined>(undefined);
  selectedVoucherType = signal<VoucherTypeEnum>(VoucherTypeEnum.FIXED_AMOUNT);

  /** Services */
  public createBriefService = inject(CreateBriefService);
  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  private currentUserService = inject(CurrentUserService);
  /** Services - END */

  constructor() {
    this.createBriefService.remunerationConfig.set({
      name: 'Cash Payment',
      type: PaymentMethodTypeEnum.CASH,
      code: null,
      description: 'Direct monetary compensation',
      value: 0,
      voucher_type: null,
      currency_code: null,
      active: true,
      created_date: new Date(),
      updated_date: new Date(),
      expiry_date: null,
      business_account_id: undefined
    })

    this.createBriefService.remunerationConfig.set({
      ...this.createBriefService.remunerationConfig()!,
      business_account_id: this.currentUserService.focussedBrand.id
    })
  }

  ngOnInit(): void {
    /**
        * 1. Check if Admin or Shout Plus User
        * 2. If Admin: 
        * -> Show Invent vs Select Model
        * 
        * 3. If Shout Plus User:
        * -> Show <!-- Shout Plus Specify Payment -->
        * 
        * 4. If Regular user:
        * -> Show default pricing model info which should already be available.
        * -> Create brief with defulat procing model that's already selected.
        */


    if (this.currentUserService.hasAnyRole([10])) {
      /** SHOUT Plus User */
      console.log('SHOUT Plus User');
      this.createBriefService.pricingUserType.set('shout_plus');
      this.remunerationOptions = this.createBriefService.configureRemunerationOptions();
      this.createBriefService.selectedRemunerationOption.set(this.remunerationOptions[0]);
      this.selectRemuneration(this.remunerationOptions[0]);
    }

    else if (this.currentUserService.hasAnyRole([2, 4, 5, 6, 7, 8, 9, 10])) {
      /** Admin User */
      console.log('Admin User');
      this.createBriefService.pricingUserType.set('admin');
      this.remunerationOptions = this.createBriefService.configureRemunerationOptions();
      this.createBriefService.selectedRemunerationOption.set(this.remunerationOptions[0]);
      this.selectRemuneration(this.remunerationOptions[0]);
    }

    else {
      /** Regular User */
      /** Use pricing model that's already selected...  */
      this.createBriefService.pricingUserType.set('regular');
      this.remunerationOptions = this.createBriefService.configureRemunerationOptions();
      this.createBriefService.selectedRemunerationOption.set(this.remunerationOptions[0]);
      this.selectRemuneration(this.remunerationOptions[0]);
    }
  }

  selectRemuneration(remuneration: CreateBriefPaymentMethodDto) {
    console.log('selectRemuneration(): ', remuneration);
    const selected: CreateBriefPaymentMethodDto | undefined = this.remunerationOptions.find(option => option.type === remuneration.type);

    this.remunerationOptions.forEach((option: CreateBriefPaymentMethodDto) => {
      option.active = option.type === remuneration.type;
    });

    if (selected) {
      this.createBriefService.selectedRemunerationOption.set(selected);
      this.createBriefService.remunerationConfig.set(selected);
    }

    console.log('Selected: ', selected);

    switch (selected?.id) {
      case 'pm_cash':
        console.log('SET -> pm_cash');
        this.createBriefService.remunerationConfig.set({
          ...this.createBriefService.remunerationConfig()!,
          name: 'Cash Payment',
          description: 'Direct monetary compensation',
          type: PaymentMethodTypeEnum.CASH,
          code: 'cash_payment',
          currency_code: this.selectedCurrency() ? this.selectedCurrency()!.code : null,
          value: 0,
          voucher_type: null,
          created_date: new Date(),
          business_account_id: this.currentUserService.focussedBrand.id
        })
        this.updatePaymentMethod();
        break;
      case 'pm_product':
        console.log('SET -> pm_product');
        this.createBriefService.remunerationConfig.set({
          ...this.createBriefService.remunerationConfig()!,
          name: 'Product',
          description: 'Free products or services',
          type: PaymentMethodTypeEnum.PRODUCT,
          code: this.createBriefService.selectedProduct()?.id,
          currency_code: this.selectedCurrency() ? this.selectedCurrency()!.code : null,
          value: 0,
          voucher_type: null,
          created_date: new Date(),
          business_account_id: this.currentUserService.focussedBrand.id
        })
        this.updatePaymentMethod();
        break;
      case 'pm_voucher':
        console.log('SET -> pm_voucher');
        this.createBriefService.remunerationConfig.set({
          ...this.createBriefService.remunerationConfig()!,
          name: 'Voucher',
          description: 'Gift cards or discount codes',
          type: PaymentMethodTypeEnum.VOUCHER,
          voucher_type: VoucherTypeEnum.PERCENTAGE,
          code: '',
          currency_code: this.selectedCurrency() ? this.selectedCurrency()!.code : null,
          value: 0,
          created_date: new Date(),
          business_account_id: this.currentUserService.focussedBrand.id
        })
        this.updatePaymentMethod();
        break;
      default:
        break;
    }
  }

  onCurrencyChange(event: any) {
    this.selectedCurrency.set({ code: event.target.value, symbol: this.currencies.find((currency) => currency.code === event.target.value)?.symbol || '' });
    console.log(this.selectedCurrency());
  }

  selectVoucherType(type: VoucherTypeEnum) {
    this.selectedVoucherType.set(type);
    this.resetDiscountValue();
  }

  getVoucherTypeClass(type: VoucherTypeEnum) {
    return this.selectedVoucherType() === type
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  updatePaymentMethod() {
    console.log('updatePaymentMethod(): ', this.createBriefService.remunerationConfig());
    this.createBriefService.briefPaymentMethodSignal.set({ ...this.createBriefService.remunerationConfig()! });
  }

  resetDiscountValue() {
    if (this.createBriefService.remunerationConfig) {
      this.createBriefService.remunerationConfig.set({
        ...this.createBriefService.remunerationConfig()!,
        value: 0
      })
    }
    this.updatePaymentMethod();
  }

  getCurrentPrice() {
    if (this.createBriefService.selectedPricingModel()) {
      const facePrice = this.createBriefService.selectedPricingModel()!.facePrice;
      const ugcPrice = this.createBriefService.selectedPricingModel()!.ugcPrice;
      const advertisingPrice = this.createBriefService.selectedPricingModel()!.advertisingPrice;
      const socialPostPrice = this.createBriefService.selectedPricingModel()!.socialPostPrice;
      const fifteenSecPrice = this.createBriefService.selectedPricingModel()!.fifteenSecPrice;
      const thirtySecPrice = this.createBriefService.selectedPricingModel()!.thirtySecPrice;
      const sixtySecPrice = this.createBriefService.selectedPricingModel()!.sixtySecPrice;
      if (this.createBriefService.selectedUgcOnly()) {
        return ugcPrice + facePrice + fifteenSecPrice + thirtySecPrice + sixtySecPrice;
      } else {
        return ugcPrice + facePrice + fifteenSecPrice + thirtySecPrice + sixtySecPrice + socialPostPrice;
      }
    }
    return 0;
  }

  getBasePrice() {
    if (this.createBriefService.selectedPricingModel()) {
      const basePrice = this.getCurrentPrice();
      return basePrice / 100;
    } else {
      return 0;
    }
  }

  getAudiencePriceExpecttaion(bonusValue: number) {
    let value = this.getBasePrice() + bonusValue / 100
    return value * this.createBriefService.videosPerCreator();
  }
}
